import { X } from "lucide-react";
import { observer } from "mobx-react-lite";
import React, { useRef, useState } from "react";

import { cn } from "@/utils/shadcn-ui";

import { Badge } from "../base/badge";

export const TagsInput = observer(function TagsInput<M extends object>({
  attr,
  model,
  className,
  placeholder,
  validate,
  formatDisplay,
}: {
  attr: keyof M;
  model: M;
  className?: string;
  /** Placeholder text shown when no tags are entered */
  placeholder?: string;
  /** Validate and optionally transform input. Return the value to add, or null to reject. */
  validate?: (value: string) => string | null;
  /** Format a tag for display (e.g., strip seconds from time) */
  formatDisplay?: (value: string) => string;
}) {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const tags = (model?.[attr] as string[]) ?? [];

  const addTag = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setInputValue("");
      return;
    }

    const validated = validate ? validate(trimmed) : trimmed;
    if (validated && !tags.includes(validated)) {
      model[attr] = [...tags, validated] as M[keyof M];
    }
    setInputValue("");
  };

  const removeTag = (tagToRemove: string) => {
    model[attr] = tags.filter((tag) => tag !== tagToRemove) as M[keyof M];
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  return (
    <div
      onClick={handleContainerClick}
      className={cn(
        "border-input focus-within:border-ring focus-within:ring-ring/50 flex min-h-8 w-full flex-wrap items-center gap-1.5 rounded-md border bg-white px-2 py-1 transition-[color,box-shadow] focus-within:ring-[3px] dark:bg-input/30",
        className,
      )}
    >
      {tags.map((tag) => (
        <Badge key={tag} variant="secondary" className="gap-1 pr-1">
          {formatDisplay ? formatDisplay(tag) : tag}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeTag(tag);
            }}
            className="hover:bg-muted-foreground/20 rounded-full p-0.5"
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => addTag(inputValue)}
        placeholder={tags.length === 0 ? placeholder : undefined}
        className="min-w-[80px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
});
